// Real Estate agent calendar — docs/real-estate/be/10-api-reference.md
// §A11. Separate from staffCalendar.js on purpose (see the Phase 3 plan's
// "copy and adapt, don't share" note): this only ever talks to
// GET /properties/agents/calendar, never the Organization staff/branch
// stores. `calendarData` is the raw `{ agents, appointments }` body —
// shaping it into FullCalendar events happens in
// routes/dashboard/realEstate/calendar/transform.js, kept pure so it can be
// unit-tested without Vuex.
import { getAgentCalendar } from '../../api/realEstate/calendar';

// Bumped per fetch so a slow earlier response (e.g. the user clicked
// "next" twice quickly, or changed a filter mid-load) can't overwrite the
// newer one when it finally lands.
let latestRequestId = 0;

function emptyFilters() {
  return { agentId: null, propertyId: null, keyword: '' };
}

export const state = {
  calendarData: { agents: [], appointments: [] },
  range: { from: null, to: null },
  filters: emptyFilters(),
  isLoading: false,
};

export const mutations = {
  setCalendarData(moduleState, data) {
    moduleState.calendarData = data;
  },
  setRange(moduleState, { from, to }) {
    moduleState.range = { from, to };
  },
  setFilters(moduleState, filters) {
    moduleState.filters = { ...moduleState.filters, ...filters };
  },
  clearFilters(moduleState) {
    moduleState.filters = emptyFilters();
  },
  setLoading(moduleState, value) {
    moduleState.isLoading = value;
  },
};

export const getters = {
  getCalendarData: moduleState => moduleState.calendarData,
  getRange: moduleState => moduleState.range,
  getFilters: moduleState => moduleState.filters,
  getIsLoading: moduleState => moduleState.isLoading,
};

export const actions = {
  // `agentId`/`propertyId` filters are sent to the server too (narrower
  // response), on top of being re-applied client-side in transform.js —
  // the server filter cuts payload size, the client one covers the
  // keyword/search case the API has no field for.
  async fetchCalendar({ commit, state: moduleState }, { from, to }) {
    latestRequestId += 1;
    const requestId = latestRequestId;
    commit('setLoading', true);
    try {
      const { agentId, propertyId } = moduleState.filters;
      const { data } = await getAgentCalendar({
        from,
        to,
        agent_id: agentId || undefined,
        property_id: propertyId || undefined,
      });
      if (requestId !== latestRequestId) return data;
      commit('setCalendarData', data);
      commit('setRange', { from, to });
      return data;
    } finally {
      if (requestId === latestRequestId) commit('setLoading', false);
    }
  },

  setFilters({ commit }, filters) {
    commit('setFilters', filters);
  },

  clearFilters({ commit }) {
    commit('clearFilters');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
